import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BlogDetailHero from './components/BlogDetailHero';
import BlogDetailContent from './components/BlogDetailContent';
import BlogDetailSidebar from './components/BlogDetailSidebar';
import BlogCard from './components/BlogCard';
import ReadingProgressBar from './components/ReadingProgressBar';
import BackToTop from './components/BackToTop';
import BlogCourseCta from './components/BlogCourseCta';
import { api } from '@/lib/api';
import { adaptArticleDetail, adaptArticlePreview, type ArticlePost } from '@/lib/articles';

const BlogDetailPage = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<ArticlePost | null>(null);
  const [posts, setPosts] = useState<ArticlePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likePending, setLikePending] = useState(false);

  const likeStorageKey = useMemo(() => {
    if (!postId) {
      return '';
    }
    return `website-article-liked:${postId}`;
  }, [postId]);

  useEffect(() => {
    if (!postId || !/^\d+$/.test(postId)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void Promise.all([
      api.getWebsiteArticleDetail(postId),
      api.searchWebsiteArticles({ pageCurrent: 1, pageSize: 100, sortBy: 'date' })
    ]).then(([detail, articlePage]) => {
      setPost(adaptArticleDetail(detail));
      setPosts(articlePage.list.map(adaptArticlePreview));
      if (typeof window !== 'undefined' && likeStorageKey) {
        setLiked(window.localStorage.getItem(likeStorageKey) === '1');
      } else {
        setLiked(false);
      }
    }).finally(() => setLoading(false));
  }, [postId, likeStorageKey]);

  const handleLike = async () => {
    if (!post || liked || likePending) {
      return;
    }
    setLikePending(true);
    try {
      const stats = await api.likeWebsiteArticle(post.id);
      const nextLikes = Number(stats.likes ?? post.likes + 1);
      const nextViews = Number(stats.views ?? post.views);
      setPost((prev) => (prev ? { ...prev, likes: nextLikes, views: nextViews } : prev));
      setPosts((prev) => prev.map((item) => (item.id === post.id ? { ...item, likes: nextLikes, views: nextViews } : item)));
      setLiked(true);
      if (typeof window !== 'undefined' && likeStorageKey) {
        window.localStorage.setItem(likeStorageKey, '1');
      }
    } finally {
      setLikePending(false);
    }
  };

  const morePosts = useMemo(() => {
    if (!post) {
      return [];
    }
    const sameCategory = posts.filter((item) => item.id !== post.id && item.category === post.category);
    const withSharedTags = posts.filter((item) => item.id !== post.id && item.category !== post.category && item.tags.some((tag) => post.tags.includes(tag)));
    const rest = posts.filter(
      (item) => item.id !== post.id && !sameCategory.find((entry) => entry.id === item.id) && !withSharedTags.find((entry) => entry.id === item.id)
    );
    return [...sameCategory, ...withSharedTags, ...rest].slice(0, 3);
  }, [post, posts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] font-sans">
        <Navbar />
        <div style={{ paddingTop: '64px' }} className="max-w-7xl mx-auto px-6 py-10">
          <div className="h-80 rounded-3xl bg-slate-100 animate-pulse mb-8" />
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_288px]">
            <div className="rounded-2xl bg-white border border-slate-100 p-8 space-y-4">
              <div className="h-8 w-2/3 rounded bg-slate-100 animate-pulse" />
              <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
              <div className="h-4 w-5/6 rounded bg-slate-100 animate-pulse" />
            </div>
            <div className="rounded-2xl bg-white border border-slate-100 p-6 space-y-3">
              <div className="h-5 w-1/2 rounded bg-slate-100 animate-pulse" />
              <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
              <div className="h-4 w-4/5 rounded bg-slate-100 animate-pulse" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
              <i className="ri-article-line text-slate-400 text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-[#0A1628] mb-2">文章不存在</h2>
            <p className="text-slate-500 text-sm mb-6">該文章可能已被移除或連結有誤</p>
            <button
              onClick={() => navigate('/blog')}
              className="px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-violet-700 transition-colors whitespace-nowrap"
            >
              返回資源中心
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FC] font-sans">
      <ReadingProgressBar />
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <BlogDetailHero post={post} />

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex gap-10">
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-slate-100 p-8">
                <BlogDetailContent post={post} liked={liked} likePending={likePending} onLike={handleLike} />
                <BlogCourseCta postCategory={post.category} />
              </div>
            </div>

            <div className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-24">
                <BlogDetailSidebar currentPost={post} posts={posts} />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-t border-slate-100 py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-violet-600 rounded-full" />
                <h3 className="text-base font-bold text-[#0A1628]">繼續閱讀</h3>
              </div>
              <button
                onClick={() => navigate('/blog')}
                className="text-xs text-violet-600 font-semibold hover:text-violet-700 cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1"
              >
                查看全部文章
                <i className="ri-arrow-right-line" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {morePosts.map((item) => (
                <BlogCard key={item.id} post={item} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default BlogDetailPage;
